/**
 * How one start-list export is cut: which rows it keeps and which columns
 * it prints. Twin of `startlist.ExportOptions`
 * (`internal/service/startlist/export.go`); the parameter names below are
 * the contract, compared off disk by `e2e/specs/startlist/export.spec.ts`.
 *
 * The options live in the page URL, so a configured view is linkable,
 * survives a reload and downloads as exactly what is on screen: the same
 * query string goes to the server.
 */

import { isID, type ID } from "./id";

export type ExportScope = "entries" | "categories";

export interface ExportOptions {
  /** One club, by id (two clubs can share a name); undefined = the whole list. */
  team?: ID;
  /** Only meaningful with `team`. */
  scope: ExportScope;
  /** ČP column. */
  cp: boolean;
  /** Start time of each row. */
  times: boolean;
  /** „Počet soutěžících". */
  count: boolean;
  /** The day's harmonogram above the running order. */
  timetable: boolean;
  /** Přeškrtnuté starts, struck through; off drops them. */
  scratched: boolean;
}

export const EXPORT_PARAMS = {
  team: "team",
  scope: "scope",
  cp: "cp",
  times: "times",
  count: "count",
  timetable: "timetable",
  scratched: "scratched",
} as const;

/** What the federation's public print carried. Same as DefaultExportOptions. */
export const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  scope: "entries",
  cp: true,
  times: true,
  count: false,
  timetable: true,
  scratched: true,
};

type Flag = "cp" | "times" | "count" | "timetable" | "scratched";
const FLAGS: Flag[] = ["cp", "times", "count", "timetable", "scratched"];

export const EXPORT_FLAG_LABELS: Record<Flag, string> = {
  cp: "Číslo přihlášky (ČP)",
  times: "Časy",
  count: "Počet soutěžících",
  timetable: "Časový harmonogram",
  scratched: "Přeškrtnuté starty",
};
export const EXPORT_FLAG_ORDER = FLAGS;

export const EXPORT_SCOPE_LABELS: Record<ExportScope, string> = {
  entries: "Jen starty týmu",
  categories: "Jen kategorie, kde tým startuje",
};

/** Reads the options; a missing or unreadable value keeps its default. */
export function parseExportOptions(params: URLSearchParams): ExportOptions {
  const o: ExportOptions = { ...DEFAULT_EXPORT_OPTIONS };
  const team = params.get(EXPORT_PARAMS.team);
  if (isID(team)) o.team = team;
  if (params.get(EXPORT_PARAMS.scope) === "categories") o.scope = "categories";
  for (const f of FLAGS) {
    const v = params.get(EXPORT_PARAMS[f]);
    if (v === "1") o[f] = true;
    else if (v === "0") o[f] = false;
  }
  return o;
}

/** Writes only what differs from the defaults, so a plain link stays plain. */
export function exportSearchParams(o: ExportOptions): URLSearchParams {
  const p = new URLSearchParams();
  if (o.team) {
    p.set(EXPORT_PARAMS.team, String(o.team));
    if (o.scope !== DEFAULT_EXPORT_OPTIONS.scope) p.set(EXPORT_PARAMS.scope, o.scope);
  }
  for (const f of FLAGS) {
    if (o[f] !== DEFAULT_EXPORT_OPTIONS[f]) p.set(EXPORT_PARAMS[f], o[f] ? "1" : "0");
  }
  return p;
}

/**
 * The part of the options the SERVER applies to the JSON document (which
 * rows exist). Column flags are the renderer's; leaving them out keeps
 * the query key, and the ETag, stable while a checkbox is toggled.
 */
export function rowFilterQuery(o: ExportOptions): string {
  const p = new URLSearchParams();
  if (o.team) {
    p.set(EXPORT_PARAMS.team, String(o.team));
    if (o.scope !== "entries") p.set(EXPORT_PARAMS.scope, o.scope);
  }
  if (!o.scratched) p.set(EXPORT_PARAMS.scratched, "0");
  return p.toString();
}
